import { gql, useMutation, Reference } from "@apollo/client";

import {
  LicensePlate,
  UpdateLicensePlate,
  UpdateLicensePlateWithNewCategoryMutation,
  UpdateLicensePlateWithNewCategoryMutationVariables,
} from "../../../../../API";
import { UPDATE_LICENSE_PLATE_WITH_NEW_CATEGORY } from "../../../../../common/operations/mutations";
import { useCustomerIdGuard } from "../../../../../common/hooks/useCustomerIdGuard";
import { handleApolloError } from "../../../../../common/utils/handleApolloError";

type UpdateLicensePlateExtented = UpdateLicensePlate & {
  newCategoryId: string;
};

export const useUpdateLicensePlate = () => {
  const customerId = useCustomerIdGuard();

  const [
    updateLicensePlateWithNewCategoryMutation,
    { data: licensePlateData, loading: licensePlateLoading },
  ] = useMutation<
    UpdateLicensePlateWithNewCategoryMutation,
    UpdateLicensePlateWithNewCategoryMutationVariables
  >(UPDATE_LICENSE_PLATE_WITH_NEW_CATEGORY);

  const updateLicensePlate = async (input: UpdateLicensePlateExtented) => {
    input.customerId = customerId;

    return await updateLicensePlateWithNewCategoryMutation({
      variables: {
        input,
      },
      onError: handleApolloError,
      update: (cache, response): void => {
        const updatedLP = response?.data?.updateLicensePlateWithNewCategory;

        if (!updatedLP) {
          return;
        }

        cache.modify({
          fields: {
            getLicensePlatesByCustomer(
              existing = { items: [] as LicensePlate[] },
              { readField }
            ) {
              const newLicensePlateRef = cache.writeFragment({
                data: updatedLP,
                fragment: gql`
                  fragment NewLicensePlate on LicensePlate {
                    id
                    categoryId
                    company
                    driverName
                    licensePlate
                  }
                `,
              });

              const returnedId = readField("id", newLicensePlateRef) as string;
              const oldId = input.licensePlateId;

              if (oldId === returnedId) {
                return {
                  ...existing,
                  items: existing.items.map((ref: Reference) => {
                    const refId = readField("id", ref) as string;

                    if (refId === returnedId) {
                      return newLicensePlateRef;
                    }

                    return ref;
                  }),
                };
              }

              return {
                ...existing,
                items: [
                  ...existing.items.filter((ref: Reference) => {
                    const refId = readField("id", ref) as string;

                    return refId !== oldId;
                  }),
                  newLicensePlateRef,
                ],
              };
            },
          },
        });
      },
    });
  };

  return {
    updateLicensePlate,
    data: licensePlateData,
    loading: licensePlateLoading,
  };
};
